import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { MagnifyingGlass } from '../../../../../Component/Icons/MagnifyingGlass'
import {
  initialProductItem,
  PLUS_CREATE_NEW_PRODUCT_ITEM_ELEM_ID,
  SCROLL_BOTTOM_BUFFER_PX,
  SEARCH_PRODUCT_LIMIT,
  SEARCH_PRODUCT_MAX_ITEMS,
} from '../../../../../Helper/EnhancedData/Constants'
import * as EDT from '../../../../../Type/EnhancedData'
import { EnhancedProductAction, EnhancedProductForm } from './EnhancedProductForm'

export interface ProductDropdownProps {
  products?: EDT.LineItemStateData[]
  onProductSelect: (rowKey: string | number, product: EDT.LineItemStateData) => void
  onProductCreate?: (rowKey: string | number, formData: EDT.LineItemStateData) => void
  onTriggerInlineCreate?: (rowKey: string | number) => void
  rowKey?: string | number
  isRowPopulated?: (rowKey: string | number) => boolean
  searchResultsMap?: Record<string | number, EDT.LineItemStateData[]>
  hasMoreProductsMap?: Record<string | number, boolean>
  hasMoreProducts?: boolean
  onLoadMore?: (rowKey: string | number, nextOffset: number) => void
  activeDropdownRowKey?: string | number | null
  setActiveDropdownRowKey?: (key: string | number | null) => void
}

interface ProductListProps {
  products: EDT.LineItemStateData[]
  onItemClick: (product: EDT.LineItemStateData) => void
  onScrollMore?: () => void
  hasMoreProducts?: boolean
}

// Exported for LineItemsTable and internal inline form usage
// TODO: Isolate this hook to a different file
export function useProductForm (initialState: EDT.LineItemStateData) {
  const [formData, setFormData] = useState<EDT.LineItemStateData>(initialState)

  const handleInputChange = (name: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }
  return { formData, handleInputChange, setFormData }
}

const ProductList = ({ products, onItemClick, onScrollMore, hasMoreProducts = true }: ProductListProps) => {
  const safeProducts = Array.isArray(products) ? products : []
  const isLoadingMoreRef = useRef(false)

  useEffect(() => {
    isLoadingMoreRef.current = false
  }, [safeProducts.length])

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget

    const isNearBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + SCROLL_BOTTOM_BUFFER_PX
    const isNotLoading = !isLoadingMoreRef.current
    const hasFullBatch = safeProducts.length >= SEARCH_PRODUCT_LIMIT
    const isUnderMaxCap = safeProducts.length < SEARCH_PRODUCT_MAX_ITEMS
    const canLoadMore = hasMoreProducts && hasFullBatch && isUnderMaxCap

    if (isNearBottom && isNotLoading && canLoadMore) {
      isLoadingMoreRef.current = true
      onScrollMore?.()
    }
  }

  return (
    <div
      onScroll={handleScroll}
      style={{
        maxHeight: '150px',
        overflowY: 'auto',
        paddingRight: '4px',
      }}
    >
      {safeProducts.map((product, index) => (
        <button
          key={product.productName || index}
          type="button"
          onClick={() => onItemClick(product)}
          className={'product-list'}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#59A2CD'
            e.currentTarget.style.color = '#ffffff'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent'
            e.currentTarget.style.color = '#64748b'
          }}
        >
          {product.productName}
        </button>
      ))}
    </div>
  )
}

const ProductDropdown = ({
  products,
  onProductSelect,
  onProductCreate,
  onTriggerInlineCreate,
  rowKey = 0,
  isRowPopulated,
  searchResultsMap,
  hasMoreProductsMap,
  hasMoreProducts,
  onLoadMore,
  activeDropdownRowKey,
  setActiveDropdownRowKey,
}: ProductDropdownProps) => {
  const [internalOpen, setInternalOpen] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const { formData, handleInputChange, setFormData } = useProductForm(initialProductItem)

  const isControlled = activeDropdownRowKey !== undefined && setActiveDropdownRowKey !== undefined
  const showDropdown = isControlled
    ? String(activeDropdownRowKey) === String(rowKey)
    : internalOpen

  const setShowDropdown = useCallback((open: boolean) => {
    if (isControlled) {
      setActiveDropdownRowKey?.(open ? rowKey : null)
    } else {
      setInternalOpen(open)
    }
  }, [isControlled, setActiveDropdownRowKey, rowKey])

  const hasExistingData = isRowPopulated ? isRowPopulated(rowKey) : false

  const buttonRef = useRef<HTMLButtonElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Safely extract products for this row or fallback to empty array
  const rowProducts = useMemo(() => {
    if (searchResultsMap && rowKey in searchResultsMap) {
      return Array.isArray(searchResultsMap[rowKey]) ? searchResultsMap[rowKey] : []
    }
    return Array.isArray(products) ? products : []
  }, [searchResultsMap, rowKey, products])

  useEffect(() => {
    if (!showDropdown && !isEditing) return

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement

      const clickedInsideButton = buttonRef.current?.contains(target)
      const clickedInsideDropdown = dropdownRef.current?.contains(target)
      const clickedInput = target?.tagName === 'INPUT'

      // Never close if user is clicking or typing in an input field
      if (!clickedInsideButton && !clickedInsideDropdown && !clickedInput) {
        setShowDropdown(false)
        setIsEditing(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showDropdown, isEditing, setShowDropdown])

  const getDropdownCoords = () => {
    if (!buttonRef.current) return { top: 0, left: 0 }
    const rect = buttonRef.current.getBoundingClientRect()
    return {
      top: rect.bottom + window.scrollY + 8,
      left: rect.right + window.scrollX - 315,
    }
  }

  const handleProductSelect = (product: EDT.LineItemStateData) => {
    setShowDropdown(false)
    onProductSelect(rowKey, product)
  }

  const handleFormSubmit = () => {
    if (!formData.productLabel?.trim()) return

    if (onProductCreate) {
      onProductCreate(rowKey, formData)
    }
    resetForm()
  }

  const resetForm = () => {
    setIsEditing(false)
    setShowDropdown(false)
    setFormData(initialProductItem)
  }

  const coords = getDropdownCoords()

  const editingFormContent = (
    <EnhancedProductForm
      productAction={hasExistingData ? EnhancedProductAction.EDIT_EXISTING : EnhancedProductAction.CREATE_NEW}
      initialValues={formData}
      changeHandler={handleInputChange}
      onSave={handleFormSubmit}
      onCancel={resetForm}
    />
  )

  const selectListContent = (
    <>
      <div style={{ marginBottom: '8px' }}>
        <a
          href=""
          className={'product-list-title'}
          id={PLUS_CREATE_NEW_PRODUCT_ITEM_ELEM_ID}
          onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline' }}
          onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none' }}
          onClick={(e) => {
            e.preventDefault()
            setShowDropdown(false)

            if (onTriggerInlineCreate) {
              onTriggerInlineCreate(rowKey)
            } else {
              setIsEditing(true)
            }
          }}
        >
          + Create New Product Item
        </a>
      </div>
      <ProductList
        products={rowProducts}
        hasMoreProducts={hasMoreProductsMap?.[rowKey] ?? hasMoreProducts ?? true}
        onItemClick={handleProductSelect}
        onScrollMore={() => onLoadMore?.(rowKey, rowProducts.length)}
      />
    </>
  )

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center' }}>
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          setShowDropdown(!showDropdown)
          setIsEditing(false)
        }}
        aria-label="View info"
        className={'hidden-button'}
      >
        <MagnifyingGlass />
      </button>

      {(showDropdown || isEditing) && createPortal(
        <div
          ref={dropdownRef}
          className={`action-menu-dropdown ${showDropdown || isEditing ? 'is-open' : ''}`}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
          style={{
            position: 'absolute',
            top: `${coords.top}px`,
            left: `${coords.left}px`,
            width: '300px',
            backgroundColor: '#ffffff',
            color: '#333333',
            border: '1px solid #cbd5e1',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            padding: '16px',
            zIndex: 99999,
            whiteSpace: 'normal',
          }}
        >
          {isEditing ? editingFormContent : selectListContent}
        </div>,
        document.body
      )}
    </div>
  )
}

export default ProductDropdown
